import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { Content, Organization } from "@/types";
import { Branch } from "@/types/branch";
import { AlertColor } from "@mui/material";

interface Snackbar {
  isOpen: boolean;
  message: string | null;
  severity: AlertColor;
}

interface UIState {
  isSummaryOpen: boolean;
  selectedMenuItem: Content | null;
  isBranchDrawerOpen: boolean;
  isLoading: boolean;
  snackbar: Snackbar;
  loading: {
    isActive: boolean;
    queue: string[];
  };
  activeOrganization: Organization["id"] | null;
  activeBranch: string | null;
  container: "fixed" | "fluid";
  isDineModeOpen: boolean;
  isLocationDialogOpen: boolean;
  isUpgradePopUpOpen: boolean;
  upgradeMessage: string;
  isNewClientFormDrawerOpen: boolean;
  isPhoneLoginDrawerOpen: boolean;
}

const initialState: UIState = {
  isSummaryOpen: false,
  selectedMenuItem: null,
  isBranchDrawerOpen: false,
  isLoading: false,
  loading: {
    isActive: false,
    queue: [],
  },
  snackbar: {
    isOpen: false,
    message: null,
    severity: "info",
  },
  activeOrganization: null,
  activeBranch: null,
  container: "fixed",
  isDineModeOpen: false,
  isLocationDialogOpen: false,
  isUpgradePopUpOpen: false,
  upgradeMessage: '',
  isNewClientFormDrawerOpen: false,
  isPhoneLoginDrawerOpen: false,
};

export const unSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    openSummary: (state) => {
      state.isSummaryOpen = true;
    },
    closeSummary: (state) => {
      state.isSummaryOpen = false;
    },
    openItem: (state, action: PayloadAction<Content>) => {
      state.selectedMenuItem = action.payload;
    },
    closeItem: (state) => {
      state.selectedMenuItem = null;
    },
    openSnackbar: (state, action: PayloadAction<Omit<Snackbar, "isOpen">>) => {
      state.snackbar = { ...action.payload, isOpen: true };
    },
    closeSnackbar: (state) => {
      state.snackbar.isOpen = false;
    },
    addLoadingTask: (state, action: PayloadAction<string>) => {
      state.loading.isActive = true;
      state.loading.queue.push(action.payload);
    },
    deleteLoadingTask: (state, action: PayloadAction<string>) => {
      const task = action.payload;
      const queue = state.loading.queue;

      if (queue.length && queue.includes(task)) {
        const newQueue = queue.filter((t) => t !== task);
        state.loading.queue = newQueue;

        if (!newQueue.length) {
          state.loading.isActive = false;
        }
      }
    },
    setActiveOrganization: (state, action: PayloadAction<Organization["id"]>) => {
      state.activeOrganization = action.payload;
    },
    setActiveBranch: (state, action: PayloadAction<string | null>) => {
      state.activeBranch = action.payload;
    },
    setContainer: (state, action: PayloadAction<"fixed" | "fluid">) => {
      state.container = action.payload;
    },
    setSelectedMenuItem: (state, action: PayloadAction<Content | null>) => {
      state.selectedMenuItem = action.payload;
    },
    setIsDineModeOpen: (state, action: PayloadAction<boolean>) => {
      state.isDineModeOpen = action.payload;
    },
    openUpgradePopUp: (state, action: PayloadAction<string>) => {
      state.isUpgradePopUpOpen = true;
      state.upgradeMessage = action.payload;
    },
    closeUpgradePopUp: (state) => {
      state.isUpgradePopUpOpen = false;
      state.upgradeMessage = '';
    },
    openLocationDialog: (state) => {
      state.isLocationDialogOpen = true;
    },
    closeLocationDialog: (state) => {
      state.isLocationDialogOpen = false;
    },
    openBranchDrawer: (state) => {
      state.isBranchDrawerOpen = true;
    },
    closeBranchDrawer: (state) => {
      state.isBranchDrawerOpen = false;
    },
    openNewClientFormDrawer: (state) => {
      state.isNewClientFormDrawerOpen = true;
    },
    closeNewClientFormDrawer: (state) => {
      state.isNewClientFormDrawerOpen = false;
    },
    openPhoneLoginDrawer: (state) => {
      state.isPhoneLoginDrawerOpen = true;
    },
    closePhoneLoginDrawer: (state) => {
      state.isPhoneLoginDrawerOpen = false;
    },
  },
});

export const {
  openSummary,
  closeSummary,
  openItem,
  closeItem,
  openBranchDrawer,
  closeBranchDrawer,
  openSnackbar,
  closeSnackbar,
  addLoadingTask,
  deleteLoadingTask,
  setActiveOrganization,
  setActiveBranch,
  setContainer,
  setSelectedMenuItem,
  setIsDineModeOpen,
  openUpgradePopUp,
  closeUpgradePopUp,
  openLocationDialog,
  closeLocationDialog,
  openNewClientFormDrawer,
  closeNewClientFormDrawer,
  openPhoneLoginDrawer,
  closePhoneLoginDrawer,
} = unSlice.actions;

export const selectedBranch = createSelector(
  [
    (state: RootState) => state.ui.activeBranch,
    (state: RootState) => state.ui.activeOrganization,
    (state: RootState) => state.organizations,
  ],
  (activeBranchId, activeOrganizationId, organizations) => {
    if (!activeBranchId || !activeOrganizationId) return null;
    const organization = organizations[activeOrganizationId];
    if (!organization || !organization.branches) return null;
    return organization.branches.find((branch: Branch) => branch.id === activeBranchId) || null;
  }
);

export default unSlice.reducer;
